/**
 * RecipeCustomizePanel Component
 * Allows users to customize recipe protein and cuisine
 */

import { useState, useEffect } from 'react'
import { Button } from '@talktoast/ui'
import { ICONS, getIconSize } from '@talktoast/shared'

interface RecipeCustomizePanelProps {
  currentProtein?: string
  currentCuisine?: string
  onApplyChanges: (modifications: { protein?: string; cuisine?: string }) => void
  isApplying?: boolean
}

const PROTEIN_OPTIONS = [
  { value: 'chicken', label: 'Chicken', emoji: '🐔' },
  { value: 'beef', label: 'Beef', emoji: '🥩' },
  { value: 'pork', label: 'Pork', emoji: '🐷' },
  { value: 'salmon', label: 'Salmon', emoji: '🐟' },
  { value: 'shrimp', label: 'Shrimp', emoji: '🦐' },
  { value: 'tofu', label: 'Tofu', emoji: '🥢' },
  { value: 'lentils', label: 'Lentils', emoji: '🌱' },
  { value: 'eggs', label: 'Eggs', emoji: '🥚' },
]

const CUISINE_OPTIONS = [
  { value: 'italian', label: 'Italian', emoji: '🇮🇹' },
  { value: 'mexican', label: 'Mexican', emoji: '🇲🇽' },
  { value: 'chinese', label: 'Chinese', emoji: '🇨🇳' },
  { value: 'japanese', label: 'Japanese', emoji: '🇯🇵' },
  { value: 'thai', label: 'Thai', emoji: '🇹🇭' },
  { value: 'indian', label: 'Indian', emoji: '🇮🇳' },
  { value: 'korean', label: 'Korean', emoji: '🇰🇷' },
  { value: 'mediterranean', label: 'Mediterranean', emoji: '🌊' },
  { value: 'french', label: 'French', emoji: '🇫🇷' },
  { value: 'american', label: 'American', emoji: '🇺🇸' },
  { value: 'greek', label: 'Greek', emoji: '🇬🇷' },
  { value: 'spanish', label: 'Spanish', emoji: '🇪🇸' },
]

export default function RecipeCustomizePanel({
  currentProtein,
  currentCuisine,
  onApplyChanges,
  isApplying = false
}: RecipeCustomizePanelProps) {
  const [selectedProtein, setSelectedProtein] = useState<string | undefined>(currentProtein)
  const [selectedCuisine, setSelectedCuisine] = useState<string | undefined>(currentCuisine)

  // Reset when current values change (e.g., after applying changes)
  useEffect(() => {
    setSelectedProtein(currentProtein)
    setSelectedCuisine(currentCuisine)
  }, [currentProtein, currentCuisine])

  const hasChanges = selectedProtein !== currentProtein || selectedCuisine !== currentCuisine

  const handleApply = () => {
    if (!hasChanges) return

    onApplyChanges({
      protein: selectedProtein,
      cuisine: selectedCuisine
    })
  }

  return (
    <div className="p-4 space-y-4 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
      {/* Protein Selection */}
      <div className="space-y-2">
        <h4 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
          <span>🥩</span>
          Protein
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PROTEIN_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedProtein(option.value)}
              disabled={isApplying}
              className={`
                px-3 py-3 rounded-lg border-2 transition-all text-sm font-medium
                flex items-center justify-center gap-1.5
                ${selectedProtein === option.value
                  ? 'border-toast-gold bg-toast-gold/10 text-toast-gold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }
                ${isApplying ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
              `}
            >
              <span>{option.emoji}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
        {selectedProtein && (
          <button
            onClick={() => setSelectedProtein(undefined)}
            disabled={isApplying}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear protein selection
          </button>
        )}
      </div>

      {/* Cuisine Selection */}
      <div className="space-y-2">
        <h4 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
          <span>🌍</span>
          Cuisine Style
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CUISINE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedCuisine(option.value)}
              disabled={isApplying}
              className={`
                px-3 py-3 rounded-lg border-2 transition-all text-sm font-medium
                flex items-center justify-center gap-1.5
                ${selectedCuisine === option.value
                  ? 'border-toast-gold bg-toast-gold/10 text-toast-gold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }
                ${isApplying ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
              `}
            >
              <span>{option.emoji}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
        {selectedCuisine && (
          <button
            onClick={() => setSelectedCuisine(undefined)}
            disabled={isApplying}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear cuisine selection
          </button>
        )}
      </div>

      {/* Apply Changes Button */}
      <div className="pt-2 border-t border-gray-200">
        <Button
          variant="primary"
          fullWidth
          onClick={handleApply}
          disabled={!hasChanges || isApplying}
          className="flex items-center justify-center gap-2"
        >
          {isApplying ? (
            <>
              <div className="animate-spin">⏳</div>
              <span>Applying Changes...</span>
            </>
          ) : (
            <>
              <ICONS.actions.refresh className={getIconSize('sm')} />
              <span>Apply Changes & Re-Roll</span>
            </>
          )}
        </Button>
        {hasChanges && !isApplying && (
          <p className="text-xs text-gray-500 text-center mt-2">
            This will generate a new recipe with your customizations
          </p>
        )}
      </div>
    </div>
  )
}
